/**
 * KioskCouponInput - Coupon code entry and validation for Kiosk checkout
 * Allows customers to enter promo/coupon codes for discounts
 */
import { useState, useEffect } from 'react';
import { Ticket, CheckCircle, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '../../utils/apiClient';

export default function KioskCouponInput({
  value,
  onChange,
  onValidCoupon,
  isAccessible = false
}) {
  const [validating, setValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);
  const [couponInfo, setCouponInfo] = useState(null);
  const [error, setError] = useState('');

  // Debounced validation
  useEffect(() => {
    if (!value || value.length < 4) {
      setCouponValid(null);
      setCouponInfo(null);
      setError('');
      onValidCoupon?.(null);
      return;
    }

    const timer = setTimeout(() => {
      validateCoupon(value);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validateCoupon = async (code) => {
    setValidating(true);
    setError('');
    
    try {
      // Check promo codes collection
      const res = await api.get(`/promo-codes/validate?code=${encodeURIComponent(code)}`);
      const data = res.data;
      
      if (data.valid) {
        setCouponValid(true);
        setCouponInfo({
          code: code,
          discount_percent: data.discount_percent || 10,
          message: data.message || `${data.discount_percent || 10}% discount applied!`
        });
        onValidCoupon?.({
          code: code,
          discount_percent: data.discount_percent || 10
        });
      } else {
        setCouponValid(false);
        setCouponInfo(null);
        setError(data.message || 'Invalid or expired code');
        onValidCoupon?.(null);
      }
    } catch (err) {
      // Fallback - check campaign signups for assigned codes
      try {
        const signupRes = await api.get(`/campaign-signups/validate-code?code=${encodeURIComponent(code)}`);
        const signupData = signupRes.data;
        
        if (signupData.valid) {
          setCouponValid(true);
          setCouponInfo({
            code: code,
            discount_percent: signupData.discount_percent || 10,
            message: signupData.message || `${signupData.discount_percent || 10}% Founding Member discount!`
          });
          onValidCoupon?.({
            code: code,
            discount_percent: signupData.discount_percent || 10
          });
        } else {
          setCouponValid(false);
          setError('Code not found or already used');
          onValidCoupon?.(null);
        }
      } catch (signupErr) {
        setCouponValid(false);
        setError('Invalid coupon code');
        onValidCoupon?.(null);
      }
    }
    
    setValidating(false);
  };

  const clearCoupon = () => {
    onChange('');
    setCouponValid(null);
    setCouponInfo(null);
    setError('');
    onValidCoupon?.(null);
  };

  return (
    <div className="bg-gray-900 rounded-3xl p-6 mb-6" data-testid="kiosk-coupon-input-section">
      <div className="flex items-center gap-3 mb-4">
        <Ticket className={`${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} text-purple-400`} />
        <label className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-white`}>
          Have a Coupon Code?
        </label>
      </div>
      
      <p className={`text-gray-400 ${isAccessible ? 'text-lg' : 'text-base'} mb-4`}>
        Enter your promotional or loyalty coupon code
      </p>
      
      <div className="relative">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          placeholder="Enter coupon code"
          className={`w-full ${isAccessible ? 'py-6 text-2xl' : 'py-4 text-xl'} bg-gray-800 border-2 rounded-2xl text-white placeholder-gray-500 focus:outline-none uppercase pr-14 ${
            couponValid === true ? 'border-green-500' :
            couponValid === false ? 'border-red-500' : 'border-gray-700'
          }`}
          data-testid="kiosk-coupon-input"
        />
        
        {/* Status Icons */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {validating && (
            <Loader2 className={`${isAccessible ? 'w-7 h-7' : 'w-6 h-6'} animate-spin text-gray-400`} />
          )}
          {!validating && couponValid === true && (
            <CheckCircle className={`${isAccessible ? 'w-7 h-7' : 'w-6 h-6'} text-green-500`} />
          )}
          {!validating && couponValid === false && value.length >= 4 && (
            <X className={`${isAccessible ? 'w-7 h-7' : 'w-6 h-6'} text-red-500`} />
          )}
          {value && (
            <button
              onClick={clearCoupon}
              className="p-1 hover:bg-gray-700 rounded-full"
              data-testid="kiosk-coupon-clear"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>
      </div>
      
      {/* Valid Coupon Message */}
      {couponValid === true && couponInfo && (
        <div className={`mt-4 p-4 bg-green-900/50 rounded-xl border border-green-700 ${isAccessible ? 'text-lg' : 'text-base'}`}>
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{couponInfo.message}</span>
          </div>
        </div>
      )}
      
      {/* Error Message */}
      {error && (
        <div className={`mt-4 p-4 bg-red-900/50 rounded-xl border border-red-700 ${isAccessible ? 'text-lg' : 'text-base'}`}>
          <span className="text-red-400">{error}</span>
        </div>
      )}
    </div>
  );
}
